/**
 * AuthenticatorInstructionsModal Component
 *
 * Modal dialog displaying instructions for enrolling Microsoft Authenticator.
 * Since Authenticator cannot be enrolled via API (requires physical device + QR code),
 * this modal guides users to enroll through Azure AD's native flow.
 */

import React from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  makeStyles,
  tokens,
  Text,
} from '@fluentui/react-components';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  explanationText: {
    color: tokens.colorNeutralForeground2,
  },
  stepsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
  },
  stepText: {
    color: tokens.colorNeutralForeground1,
  },
});

interface AuthenticatorInstructionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AuthenticatorInstructionsModal: React.FC<
  AuthenticatorInstructionsModalProps
> = ({ isOpen, onClose }) => {
  const styles = useStyles();

  const handleOpenAzureAD = () => {
    // Open Azure AD security settings in new tab
    window.open('https://mysignins.microsoft.com/security-info', '_blank');
  };

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Set Up Microsoft Authenticator</DialogTitle>
          <DialogContent className={styles.content}>
            <Text className={styles.explanationText}>
              Microsoft Authenticator must be set up directly through Azure AD
              using your mobile device.
            </Text>

            <div>
              <Text weight="semibold">Steps to enroll:</Text>
              <ol className={styles.stepsContainer}>
                <li>
                  <Text className={styles.stepText}>
                    Install Microsoft Authenticator app on your phone
                  </Text>
                </li>
                <li>
                  <Text className={styles.stepText}>
                    Click &ldquo;Open Azure AD Settings&rdquo; below
                  </Text>
                </li>
                <li>
                  <Text className={styles.stepText}>
                    Select &ldquo;Add sign-in method&rdquo;
                  </Text>
                </li>
                <li>
                  <Text className={styles.stepText}>
                    Choose &ldquo;Authenticator app&rdquo;
                  </Text>
                </li>
                <li>
                  <Text className={styles.stepText}>
                    Follow the QR code scanning process
                  </Text>
                </li>
              </ol>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              Close
            </Button>
            <Button appearance="primary" onClick={handleOpenAzureAD}>
              Open Azure AD Settings
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
